// src/pages/localization/LocalizationSettingsPage.tsx
// Admin: locales, platform locale config (default/available/fallback chain),
// coverage stats, namespaces registry. Tabbed, mirrors CalendarSettingsPage.
// Replaces the generated CRUD scaffold — see docs/ux/localization-ia.md §3.

import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { LocalesSettings } from '@/features/localization/LocalesSettings';
import { PlatformConfigSettings } from '@/features/localization/PlatformConfigSettings';
import { CoverageSettings } from '@/features/localization/CoverageSettings';
import { NamespacesSettings } from '@/features/localization/NamespacesSettings';

type Tab = 'locales' | 'config' | 'coverage' | 'namespaces';
const TABS: Tab[] = ['locales', 'config', 'coverage', 'namespaces'];

export const LocalizationSettingsPage = observer(() => {
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();

  const tab = useMemo<Tab>(() => {
    const v = searchParams.get('tab');
    return (TABS as string[]).includes(v ?? '') ? (v as Tab) : 'locales';
  }, [searchParams]);

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between">
        {/* The tab already names this page; the heading stays for screen readers. */}
        <h1 className="sr-only">{t('localization.settingsTitle')}</h1>
        <Link to="/localization" className="ml-auto text-sm text-primary hover:underline">{t('localization.keys.backToKeys')}</Link>
      </div>

      <div role="tablist" aria-label={t('localization.settingsTitle')} className="flex flex-wrap gap-1 border-b border-border">
        {TABS.map((v) => (
          <button
            key={v}
            role="tab"
            aria-selected={tab === v}
            onClick={() => setSearchParams({ tab: v })}
            className={`-mb-px border-b-2 px-3 py-2 text-sm ${
              tab === v ? 'border-primary font-medium text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t(`localization.settingsTab.${v}`)}
          </button>
        ))}
      </div>

      {tab === 'locales' && <LocalesSettings />}
      {tab === 'config' && <PlatformConfigSettings />}
      {tab === 'coverage' && <CoverageSettings />}
      {tab === 'namespaces' && <NamespacesSettings />}
    </div>
  );
});
